// TradingJournal.js
import React, { useState, useEffect } from 'react';
import { Goal, Skull, Calendar, TrendingUp, TrendingDown, Target, ChevronLeft, ChevronRight, Info } from 'lucide-react';

export const TradingJournal = ({ stockName, scoreHistory = [], simulationResults = [], monitoringDayCount = 0, totalProfit = 0}) => {
  const [showTooltip, setShowTooltip] = useState(null);
  // Transform scoreHistory data to journal format
  const transformScoreHistoryToJournal = (scoreHistory) => {
    if (!scoreHistory || !Array.isArray(scoreHistory)) {
      console.warn('scoreHistory is not a valid array:', scoreHistory);
      return [];
    }

    const transformed = scoreHistory.map((item, index) => {
      let actual = 'neutral';
      let actualPercent = 0;
      
      // Check if required fields exist
      if (!item.scoreDate) {
        console.warn('Missing scoreDate in item:', item);
        return null;
      }
      
      if (item.afterCloseStockPrice && item.beforeCloseStockPrice) {
        const priceChange = item.afterCloseStockPrice - item.beforeCloseStockPrice;
        actualPercent = ((priceChange / item.beforeCloseStockPrice) * 100);
        actual = priceChange > 0 ? 'up' : priceChange < 0 ? 'down' : 'neutral';
      } else {
        console.warn('Missing stock price data in item:', item);
      }

      // Determine if prediction was correct based on score and actual movement
      const isCorrect = (item.score >= 0 && actual === 'up') || (item.score < 0 && actual === 'down');

      const result = {
        date: item.scoreDate.split('T')[0], // Extract date from ISO string
        actual: actual,
        actualPercent: Math.round(actualPercent * 100) / 100, // Round to 2 decimal places
        prediction: item.score || 0,
        isCorrect: isCorrect,
        status: item.status, // Add status from API
        aiModelId: item.aiModelId || null
      };
      
      return result;
    })
    .filter(item => item !== null)
    //.filter(item => Math.abs(item.prediction) >= 70); // 예측 점수가 70 이상인 것만
    
    const filtered = transformed.filter(item => item.actual !== 'neutral');

    // Sort by date in descending order (newest first)
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Use state to handle data updates
  const [journalData, setJournalData] = useState([
  ]);

  // Update journal data when scoreHistory changes
  useEffect(() => {
    if (scoreHistory && scoreHistory.length > 0) {
      const transformedData = transformScoreHistoryToJournal(scoreHistory);
      if (transformedData.length > 0) {
        setJournalData(transformedData);
      } else {
        console.warn('Transformed data is empty, keeping fallback data');
      }
    }
  }, [scoreHistory]);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const totalPages = Math.ceil(journalData.length / itemsPerPage);

  // stockName이 변경될 때 currentPage를 1로 초기화
  useEffect(() => {
    setCurrentPage(1);
  }, [stockName]);

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return journalData.slice(startIndex, endIndex);
  };

  const formatDateYear = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: "numeric"
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
    });
  };

  const formatDateDay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      day: 'numeric',
      weekday: 'short'
    });
  };

  const getActualStatusIcon = (actual) => {
    return actual === 'up' ? (
      <TrendingUp size={16} className="text-green-500" />
    ) : (
      <TrendingDown size={16} className="text-red-500" />
    );
  };

  const getActualStatusColor = (actual) => {
    return actual === 'up' ? 'text-green-500' : 'text-red-500';
  };

  const getPredictionColor = (prediction) => {
    if (prediction > 0) return 'text-green-500';
    if (prediction < 0) return 'text-red-500';
    return 'text-gray-500';
  };

  const getAccuracyColor = (isCorrect) => {
    return isCorrect ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600';
  };

  const getStatusDisplay = (status) => {
    switch(status) {
      case 'SELL': return { text: '매도', color: 'text-red-600' };
      case 'BUY': return { text: '매수', color: 'text-green-600' };
      case 'HOLDING': return { text: '보유', color: 'text-blue-600' };
      case 'NO_POSITION': return { text: '미보유', color: 'text-gray-500' };
      default: return { text: '미보유', color: 'text-gray-500' };
    }
  };

  const getModelIcon = (aiModelId) => {
    if (!aiModelId) return null;
    const lower = aiModelId.toLowerCase();
    if (lower.includes('gpt')) {
      return <img src="/LLMLogo/gpt.svg" alt="GPT" className="w-5 h-5 object-contain" />;
    }
    if (lower.includes('gemini')) {
      return <img src="/LLMLogo/gemini.svg" alt="Gemini" className="w-5 h-5 object-contain" />;
    }
    return null;
  };

  const getRowBackgroundColor = (status) => {
    switch(status) {
      case 'SELL': return 'bg-pink-100';
      case 'BUY': return 'bg-green-100';
      case 'HOLDING': return 'bg-blue-50';
      case 'NO_POSITION': return 'bg-white';
      default: return 'bg-white';
    }
  };

  // 페이지네이션 번호 생성 함수 (축약형)
  const generatePageNumbers = (currentPage, totalPages) => {
    const pages = [];
    
    if (totalPages <= 7) {
      // 페이지가 7개 이하면 모든 페이지 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 페이지가 많을 때 축약형 적용
      if (currentPage <= 4) {
        // 현재 페이지가 앞쪽에 있을 때: 1 2 3 4 5 ... 11
        pages.push(1, 2, 3, 4, 5, '...', totalPages);
      } else if (currentPage >= totalPages - 3) {
        // 현재 페이지가 뒤쪽에 있을 때: 1 ... 7 8 9 10 11
        pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // 현재 페이지가 중간에 있을 때: 1 ... 4 5 6 ... 11
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div>

      {/* 통계 요약 */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-1 mb-1">
            <p className="text-xs text-gray-500 mb-1">거래 추적일</p>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-2xl font-bold text-gray-900">{monitoringDayCount}일</p>
            <Calendar className="text-gray-400" size={20} />
          </div>
        </div>

        <div className={`rounded-lg p-4 ${totalProfit >= 0 ? 'bg-blue-50' : 'bg-pink-100'}`}>
          <div className="flex items-center gap-1 mb-1">
            <p className={`text-xs ${totalProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>시뮬레이션 수익률</p>
            <button
              className="relative"
              onMouseEnter={() => setShowTooltip('simulation')}
              onMouseLeave={() => setShowTooltip(null)}
            >
              <Info size={10} className={`hover:opacity-80 ${totalProfit >= 0 ? 'text-blue-400' : 'text-red-400'}`} />
              {showTooltip === 'simulation' && (
                <div className="absolute bottom-full right-0 mb-2 w-48 p-3 bg-gray-800 text-white text-xs rounded shadow-lg z-10">
                  <div className="mb-2 font-semibold">💎 시뮬레이션 수익률이란?</div>
                  <div className="text-gray-200">
                    AI 분석점수가 70점 이상일 때 매수, -70이하일 때 매도하여 최초 분석부터 누적한 매매 수익률입니다.
                  </div>
                </div>
              )}
            </button>
          </div>
          <div className="flex items-center justify-between">
            <p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              {totalProfit
                ? `${totalProfit > 0 ? '+' : ''}${(totalProfit * 100).toFixed(2)}%`
                : '-%'
              }
            </p>
          </div>
        </div>
      </div>


      {/* 테이블 */}
      <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100">
        {/* 테이블 헤더 */}
        <div className="bg-gray-50 px-3 py-4 border-b border-gray-200">
          <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
            <div className="col-span-3 text-center">날짜</div>
            <div className="col-span-3 text-center">실제 주가</div>
            <div className="col-span-2 text-center">점수</div>
            <div className="col-span-2 text-center">상태</div>
            <div className="col-span-2 text-center">모델</div>
          </div>
        </div>

        {/* 테이블 바디 */}
        <div className="divide-y divide-gray-150">
          {getCurrentPageData().map((item, index) => (
            <div key={item.date} className={`px-3 py-4 hover:opacity-80 transition-colors ${getRowBackgroundColor(item.status)}`}>
              <div className="grid grid-cols-12 gap-4 items-center">
                {/* 날짜 */}
                <div className="col-span-3 text-center">
                  <div>
                    <p className="text-[11px] font-medium text-gray-900">{formatDateYear(item.date)} {formatDate(item.date)}</p>
                    <p className="text-[11px] text-gray-500">{formatDateDay(item.date)}</p>
                  </div>
                </div>

                {/* 실제 주가 */}
                <div className="col-span-3">
                  <div className="flex items-center gap-1">
                    {getActualStatusIcon(item.actual)}
                    <p className={`text-xs ${getActualStatusColor(item.actual)}`}>
                      {item.actualPercent > 0 ? '+' : ''}{item.actualPercent}%
                    </p>
                  </div>
                </div>

                {/* AI 점수 */}
                <div className="col-span-2 flex items-center justify-center">
                  <p className={`text-xs ${getPredictionColor(item.prediction)}`}>
                    {item.prediction > 0 ? '+' : ''}{item.prediction}
                  </p>
                </div>

                {/* 상태 */}
                <div className="col-span-2 flex items-center justify-center">
                  <span className={`text-xs ${getStatusDisplay(item.status).color}`}>
                    {getStatusDisplay(item.status).text}
                  </span>
                </div>

                {/* 분석모델 */}
                <div className="col-span-2 flex items-center justify-center">
                  {getModelIcon(item.aiModelId)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-gray-500"> </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>

            <div className="flex space-x-1">
              {generatePageNumbers(currentPage, totalPages).map((page, index) => {
                if (page === '...') {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 py-1 text-xs font-medium text-gray-400"
                    >
                      ...
                    </span>
                  );
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// TradingJournalMobile.js - 모바일용 카드 스타일
const TradingJournalMobile = () => {
  const [journalData] = useState([
    
  ]);

  return (
    <div className="feature-section">
      <div className="feature-icon">
        <Calendar size={24} />
      </div>
      <h2 className="text-xl font-bold mb-2 text-black">매매일지</h2>
      <p className="text-gray-600 mb-6">
        실제 주가 움직임과 AI 예측을 비교하여 투자 성과를 분석하고 개선점을 찾아보세요.
      </p>

      <div className="space-y-4">
        {journalData.map((item, index) => (
          <div key={item.date} className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-gray-900">{item.date}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${item.isCorrect ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                }`}>
                {item.isCorrect ? '정확' : '불일치'}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500 mb-1">실제 주가</p>
                <div className="flex items-center space-x-2">
                  {item.actual === 'up' ? (
                    <TrendingUp size={16} className="text-green-500" />
                  ) : (
                    <TrendingDown size={16} className="text-red-500" />
                  )}
                  <span className={`text-sm font-semibold ${item.actual === 'up' ? 'text-green-500' : 'text-red-500'
                    }`}>
                    {item.actual === 'up' ? '상승' : '하락'} ({item.actualPercent > 0 ? '+' : ''}{item.actualPercent}%)
                  </span>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">AI 예측</p>
                <div className="flex items-center space-x-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${item.prediction > 0 ? 'bg-green-500' : item.prediction < 0 ? 'bg-red-500' : 'bg-gray-500'
                    }`}>
                    {Math.abs(item.prediction)}
                  </div>
                  <span className={`text-sm font-semibold ${item.prediction > 0 ? 'text-green-500' : item.prediction < 0 ? 'text-red-500' : 'text-gray-500'
                    }`}>
                    {item.prediction > 0 ? '+' : ''}{item.prediction}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TradingJournal;

